<template>
  <div class="footer">
    <field-popup :confirmField="nextAction && nextAction.action" />

    <div class="footer__core">
      <p class="footer__info float-left">{{ $tc('footer.toComplete', this.outstandingFieldsNo) }}</p>

      <div class="footer__buttons">
        <BaseButton
          v-if="nextAction"
          @click.native="nextAction.action"
          variant="secondary"
          :disabled="!nextAction.action"
          :data-cy="nextAction.type === 'confirm' ? 'confirmField' : 'field-next'"
        >
          <span class="button__text">{{ nextAction.text }}</span>
        </BaseButton>
        <BaseButton
          @click.native="submit"
          :disabled="!isEnvelopeComplete"
          data-cy="submit"
        >
          {{ submitText }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/library/buttons/BaseButton.vue'
import FieldPopup from '@/components/FieldPopup'
import { mapGetters, mapActions } from 'vuex'
import { submitEnvelope } from '@/store/api'

export default {
  name: 'document-footer',
  components: {
    BaseButton,
    FieldPopup
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters([
      'outstandingFieldsNo',
      'activeFieldId',
      'isEnvelopeComplete',
      'isDocumentDone',
      'nextIncompleteDocumentIndex',
      'nextField',
      'allowNextField',
      'onLastField',
      'fieldDone',
      'getField',
      'hasSignature',
      'document',
      'config'
    ]),
    currentField () {
      return this.getField(this.activeFieldId) || {}
    },
    nextFieldText () {
      if (this.nextField) {
        const hasTitle = this.nextField.title !== 'Untitled'
        const buttonText = hasTitle
          ? this.nextField.title
          : this.$t(`fieldPopup.type.${this.nextField.type}`)
        return this.$t('footer.nextField', { field: buttonText })
      }

      return ''
    },
    nextDocumentText () {
      if (this.nextIncompleteDocumentIndex > -1) {
        const doc = this.document(this.nextIncompleteDocumentIndex)
        const hasTitle = doc.title !== 'Untitled'
        const buttonText = hasTitle
          ? doc.title
          : 'document'
        return this.$t('footer.nextField', { field: buttonText })
      }

      return ''
    },
    nextAction () {
      if (this.currentField.type === 'signature') {
        return {
          type: 'confirm',
          text: this.$t('functional.confirm'),
          action: this.hasSignature ? this.confirmField : undefined
        }
      } else if (this.nextField || (this.currentField.id && !this.onLastField)) {
        return {
          type: 'field',
          text: this.nextFieldText,
          action: this.allowNextField ? this.gotoNextField : undefined
        }
      } else if (this.nextIncompleteDocumentIndex > -1) {
        return {
          type: 'document',
          text: this.nextDocumentText,
          action: this.isDocumentDone() ? this.gotoNextDocument : undefined
        }
      }

      return null
    },
    submitText (state) {
      return state.config('company_consumer_regulations') === '1'
        ? this.$t('footer.order')
        : this.$t('footer.submit')
    }
  },
  methods: {
    ...mapActions(['setActiveField', 'nextDocument', 'confirmSignature']),
    isThisLegal () {
      this.$bus.$emit('openModal', 'helpModal')
      this.$bus.$emit('selectTab', 'is-this-legal')
    },
    question () {
      this.$bus.$emit('openModal', 'helpModal')
      this.$bus.$emit('selectTab', 'contact-signable-team')
    },

    confirmField () {
      if (!this.hasSignature) {
        return
      }

      this.confirmSignature(this.activeFieldId)

      this.gotoNextField()
    },
    gotoNextField (event) {
      if (this.currentField.type === 'textarea' && event.keyCode && event.keyCode === 13) {
        return
      }

      this.setActiveField(this.nextField ? this.nextField.id : null)
      this.$bus.$emit('scrollBody')
    },
    gotoNextDocument (event) {
      if (this.currentField.type === 'textarea' && event.keyCode && event.keyCode === 13) {
        return
      }

      this.setActiveField(null)
      this.nextDocument()
    },
    submit () {
      if (this.isEnvelopeComplete) {
        if (this.isPreview) {
          alert(this.$t('footer.signPreviewWarning'))
        } else if (this.$store.getters.isWidget) {
          this.$bus.$emit('openModal', 'widgetPartyInfoModal')
        } else {
          this.$bus.$emit('loading')

          submitEnvelope({ store: this.$store }).then((response) => {
            this.$bus.$emit('submitEnvelope', { response })
          })
        }
      }
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  .footer
    background-color: #fff
    position: fixed
    bottom: 0
    right: 0
    width: 75%
    box-shadow: 0px -15px 15px -20px $color-grey
    vertical-align: middle
    &__core
      padding: $spacing-base
      min-height: 75px
      display: flex
      flex-wrap: wrap
      justify-content: end
      align-items: center
      gap: $spacing-base
    &__info
      font-weight: bold
      margin: 0
    &__buttons
      display: flex
      gap: $spacing-base
  // Responsive
  @media screen and (max-width: $screen-size-large)
    .footer
      width: 100%
      &__help-buttons
        display: none
  @media screen and (max-width: 480px)
    .footer__buttons
      flex-direction: column
      gap: $spacing-small
      width: 100%
    .footer .footer__info
      width: 100%
      text-align: left
    .field-popup
      position: initial
    .footer__buttons .button__text
      display: block
      max-width: 100%
      overflow: hidden
      text-overflow: ellipsis
      white-space: nowrap
</style>
